import React, {useContext, useState} from 'react';

import {ApiRoutes, FetchContext, HttpMethods, ToastContext} from '../../contexts';

import {useAggregatedState} from '../../../helpers';

export const ComuniContext = React.createContext(null);

const {Provider} = ComuniContext;

export const ComuniProvider = (props) => {
    const {
        showWarningMessage,
        showSuccessMessage,
        showErrorMessage,
        showLoadingMessage,
        closeLoadingMessage,
    } = useContext(ToastContext);
    const {fetchOk, fetchAuthorized} = useContext(FetchContext);
    const [state, setState] = useAggregatedState({
        comuni: null,
        regioni: null,
        regioniOptions: [],
        province: null,
        provinceOptions: [],
        editProvince: null,
        editProvinceOptions: [],
        selectedComuni: [],
    });

    const [isLoadingComuni, setIsLoadingComuni] = useState(false);
    const getComuniAsync = async (pageIndex = 0, pageSize = 100, term, provinciaId, regioneId, valid = false, orphans = false, notANPR = false, date = null, noCAP = null) => {
        try {
            setIsLoadingComuni(true);
            const r = await fetchAuthorized(ApiRoutes.Comune.GetComuni({
                pageIndex,
                pageSize,
                term,
                provinciaId,
                regioneId,
                valid,
                orphans,
                fromANPR: notANPR ? false : null,
                date,
                noCAP
            }), {
                method: HttpMethods.GET
            });
            const list = await r.json();
            list.result.forEach(x => {
                x.dataIstituzione = new Date(x.dataIstituzione);
                x.dataCessazione = x.dataCessazione ? new Date(x.dataCessazione) : null;
                if (x.provincia) {
                    x.provincia.inizioValidita = new Date(x.provincia.inizioValidita);
                    x.provincia.fineValidita = x.provincia.fineValidita ? new Date(x.provincia.fineValidita) : null;
                    if (x.provincia.regione) {
                        x.provincia.regione.inizioValidita = new Date(x.provincia.regione.inizioValidita);
                        x.provincia.regione.fineValidita = x.provincia.regione.fineValidita ? new Date(x.provincia.regione.fineValidita) : null;
                    }
                }
            })
            setState({comuni: list, selectedComuni: []});
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingComuni(false);
        }
    }

    const [isImportingComuni, setIsImportingComuni] = useState(false);
    const importComuniAsync = async (data) => {
        try {
            setIsImportingComuni(true);
            if (window.confirm("Confermare l'importazione?")) {
                showLoadingMessage("Importazione comuni in corso...", "ImportComuni",);
                const formData = new FormData();
                formData.append("file", data);
                const r = await fetchAuthorized(ApiRoutes.Comune.ImportComuni(), {
                    method: HttpMethods.POST,
                    body: formData,
                });
                const result = await r.json();
                closeLoadingMessage("ImportComuni");
                showSuccessMessage("Importazione comuni completata");
            }
        } catch (e) {
            console.debug(e);
            closeLoadingMessage("ImportComuni");
        } finally {
            setIsImportingComuni(false);
        }
    }

    const [isAutoImportingComuni, setIsAutoImportingComuni] = useState(false);
    const autoImportComuniAsync = async () => {
        try {
            setIsAutoImportingComuni(true);
            if (window.confirm("Confermare l'importazione automatica?")) {
                showLoadingMessage("Importazione comuni in corso...", "ImportComuni",);
                const r = await fetchAuthorized(ApiRoutes.Comune.AutoImportComuni(), {
                    method: HttpMethods.POST
                });
                const result = await r.json();
                closeLoadingMessage("ImportComuni");
                showSuccessMessage("Importazione comuni completata");
            }
        } catch (e) {
            console.debug(e);
            closeLoadingMessage("ImportComuni");
        } finally {
            setIsAutoImportingComuni(false);
        }
    }

    const [isImportingCAP, setIsImportingCAP] = useState(false);
    const importCAPAsync = async (data) => {
        try {
            setIsImportingCAP(true);
            if (window.confirm("Confermare l'importazione?")) {
                showLoadingMessage("Importazione CAP in corso...", "ImportCAP",);
                const formData = new FormData();
                formData.append("file", data);
                const r = await fetchAuthorized(ApiRoutes.Comune.ImportCAP(), {
                    method: HttpMethods.POST,
                    body: formData,
                });
                const result = await r.json();
                closeLoadingMessage("ImportCAP");
                showSuccessMessage("Importazione CAP completata");
            }
        } catch (e) {
            console.debug(e);
            closeLoadingMessage("ImportCAP");
        } finally {
            setIsImportingCAP(false);
        }
    }

    const [isAutoImportingCAP, setIsAutoImportingCAP] = useState(false);
    const autoImportCAPAsync = async () => {
        try {
            setIsAutoImportingCAP(true);
            if (window.confirm("Confermare l'importazione automatica?")) {
                showLoadingMessage("Importazione CAP in corso...", "ImportCAP",);
                const r = await fetchAuthorized(ApiRoutes.Comune.AutoImportCAP(), {
                    method: HttpMethods.POST
                });
                const result = await r.json();
                closeLoadingMessage("ImportCAP");
                showSuccessMessage("Importazione CAP completata");
            }
        } catch (e) {
            console.debug(e);
            closeLoadingMessage("ImportCAP");
        } finally {
            setIsAutoImportingCAP(false);
        }
    }

    const [isLoadingRegioni, setIsLoadingRegioni] = useState(false);
    const getRegioniAsync = async (term = null, pageIndex = 0, pageSize = 1000) => {
        try {
            setIsLoadingRegioni(true);
            const r = await fetchAuthorized(ApiRoutes.Regione.GetRegioni({term, pageIndex, pageSize}), {
                method: HttpMethods.GET
            });
            const list = await r.json();
            setState({
                regioni: list, regioniOptions: list.result.map(e => {
                    return {regioneId: e.id, label: `${e.descrizione}`}
                }).sort((a, b) => a.label.localeCompare(b.label))
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingRegioni(false);
        }
    }

    const [isLoadingProvince, setIsLoadingProvince] = useState(false);
    const getProvinceAsync = async (term = null, pageIndex = 0, pageSize = 1000, regioneId = null) => {
        try {
            setIsLoadingProvince(true);
            const r = await fetchAuthorized(ApiRoutes.Provincia.GetProvince({term, pageIndex, pageSize, regioneId}), {
                method: HttpMethods.GET
            });
            const list = await r.json();
            list.result.forEach(x => {
                x.inizioValidita = new Date(x.inizioValidita);
                x.fineValidita = x.fineValidita ? new Date(x.fineValidita) : null;
            })
            setState({
                province: list, provinceOptions: list.result.map(e => {
                    return {
                        provinciaId: e.id,
                        label: `${e.descrizione} (${e.sigla}) ${e.fineValidita ? `(${e.fineValidita.getDate()}-${e.fineValidita.getMonth() + 1}-${e.fineValidita.getFullYear()})` : ""}`
                    }
                }).sort((a, b) => a.label.localeCompare(b.label))
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingProvince(false);
        }
    }

    const [isLoadingEditProvince, setIsLoadingEditProvince] = useState(false);
    const getEditProvinceAsync = async (term = null, pageIndex = 0, pageSize = 1000) => {
        try {
            setIsLoadingEditProvince(true);
            const r = await fetchAuthorized(ApiRoutes.Provincia.GetProvince({term, pageIndex, pageSize}), {
                method: HttpMethods.GET
            });
            const list = await r.json();
            setState({
                editProvince: list, editProvinceOptions: list.result.map(e => {
                    return {provinciaId: e.id, label: `${e.descrizione} (${e.sigla})`}
                }).sort((a, b) => a.label.localeCompare(b.label))
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingEditProvince(false);
        }
    }

    const [isUpdatingComuni, setIsUpdatingComuni] = useState(false);
    const updateComuniAsync = async (comuni) => {
        try {
            setIsUpdatingComuni(true);
            if (window.confirm("Confermare la modifica?")) {
                const result = await fetchAuthorized(ApiRoutes.Comune.UpdateComuni(), {
                    method: HttpMethods.POST,
                    body: JSON.stringify(comuni),
                });
                showSuccessMessage("Comuni aggiornati");
            }
        } catch (e) {
            console.debug(e);
        } finally {
            setIsUpdatingComuni(false);
        }
    }

    return (
        <Provider value={{
            state,
            setState,

            isLoadingComuni,
            getComuniAsync,

            isImportingComuni,
            importComuniAsync,

            isAutoImportingComuni,
            autoImportComuniAsync,

            isImportingCAP,
            importCAPAsync,

            isAutoImportingCAP,
            autoImportCAPAsync,

            isLoadingRegioni,
            getRegioniAsync,

            isLoadingEditProvince,
            getEditProvinceAsync,

            isLoadingProvince,
            getProvinceAsync,

            isUpdatingComuni,
            updateComuniAsync,
        }}>
            {props.children}
        </Provider>
    );
}